import React from 'react'
import analytics from 'global/AnalyticsData'

/**
 * Language switcher as a code component (BR-5533).
 *
 * Replaces the Puck block of the same name. The block asked an author to list every
 * translation by slug inside one page's content: unvalidated, maintained by hand, and
 * with no guarantee both sides pointed at each other. The alternates now come from the
 * page record's translation key, worked out at build time.
 *
 * Renders nothing when a page has no translations, which is most pages. A switcher
 * offering a language that does not exist is worse than no switcher.
 *
 * @param {object}   props
 * @param {Array}    props.alternates  [{ lang, hreflang, path }], from page context
 * @param {string}   props.activeLang  the language code of the page being viewed
 */
export const LANGUAGE_COOKIE = 'db-lang-pref'

// One year. The point is that an explicit choice survives, so it has to outlive the
// session; detection only applies to visitors who have not chosen.
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365

const LABELS = {
  en: 'English',
  es: 'Español',
}

/**
 * Remember the choice so nginx can honour it on a later unprefixed URL.
 *
 * The value is the language code, which is also the URL prefix nginx redirects to and the
 * code stored on the page. One spelling, so there is nothing here that can disagree with
 * the rest of the stack.
 */
export function rememberLanguage(lang) {
  if (typeof document === 'undefined') return
  document.cookie = `${LANGUAGE_COOKIE}=${encodeURIComponent(
    lang,
  )};path=/;max-age=${COOKIE_MAX_AGE};samesite=lax`
}

export const LANGUAGE_SWITCH_KEY = 'db-lang-switch'

/**
 * Record the switch for the page that is about to load, instead of reporting it here.
 *
 * The link is a real navigation, so the browser starts unloading on the same click. The
 * dataLayer push is synchronous, but the tag that reads it is not — GTM evaluates and
 * sends on its own queue, and that queue does not survive the unload. Reported from here,
 * the event was lost every time.
 *
 * Handing it to the next page load removes the race outright: no beacon transport, and no
 * artificial delay in front of the navigation.
 */
export function rememberLanguageSwitch(from, to) {
  try {
    window.sessionStorage.setItem(LANGUAGE_SWITCH_KEY, JSON.stringify({ from, to }))
  } catch {
    // Private mode and a full quota both throw. Losing the event is acceptable here;
    // breaking the navigation is not.
  }
}

/**
 * Emit a switch recorded by the previous page, once.
 *
 * `currentLang` guards against a stale record. A middle-click, an opened-in-new-tab, or a
 * cancelled navigation leaves the flag set without ever landing on the target language,
 * and it would otherwise fire on whatever page the reader opened next.
 */
export function flushLanguageSwitch(currentLang) {
  let raw = null
  try {
    raw = window.sessionStorage.getItem(LANGUAGE_SWITCH_KEY)
    if (raw) window.sessionStorage.removeItem(LANGUAGE_SWITCH_KEY)
  } catch {
    return
  }
  if (!raw) return

  let pending = null
  try {
    pending = JSON.parse(raw)
  } catch {
    return
  }
  if (!pending?.to || pending.to !== currentLang) return

  // `from` and `to` both go on the event because the interesting question is the
  // direction people switch in, not just which language wins.
  analytics.trackEvent('language_switch', {
    break: { language: pending.to },
    language_from: pending.from,
    language_to: pending.to,
  })
}

/**
 * @param {object} props.classes  Class names from the consuming site, so the site owns how
 *                               this looks while the theme keeps owning what it does
 *                               (which languages exist, and remembering the choice).
 *                               Falls back to plain inline styling when not given, which
 *                               is enough to be usable but is not anybody's design.
 */
const LanguageSwitcher = ({ alternates = [], activeLang, classes = {} }) => {
  if (!alternates || alternates.length < 2) return null

  const styled = Object.keys(classes).length > 0

  return (
    <nav aria-label="Language" className={classes.nav}>
      <ul
        className={classes.list}
        style={
          styled
            ? undefined
            : {
                display: 'flex',
                gap: '0.75rem',
                listStyle: 'none',
                margin: 0,
                padding: 0,
              }
        }
      >
        {alternates.map(alternate => {
          const isActive = alternate.lang === activeLang
          return (
            <li key={alternate.lang} className={classes.item}>
              <a
                href={alternate.path}
                hrefLang={alternate.hreflang}
                aria-current={isActive ? 'true' : undefined}
                onClick={() => {
                  rememberLanguage(alternate.lang)
                  rememberLanguageSwitch(activeLang, alternate.lang)
                }}
                className={
                  [classes.link, isActive ? classes.active : classes.inactive]
                    .filter(Boolean)
                    .join(' ') || undefined
                }
                style={styled ? undefined : { fontWeight: isActive ? 700 : 400 }}
              >
                {LABELS[alternate.lang] || alternate.lang}
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

export default LanguageSwitcher
